import { useCallback } from "react";
import type { ExcalidrawElement } from "@excalidraw/element/types";
import type { AppState } from "../../types";
import type { Scene } from "@excalidraw/element";
import { applySpacingToElements, getSpacingBetweenElements } from "@excalidraw/element/spacing";
import { CaptureUpdateAction } from "@excalidraw/element";
import { arrayToMap } from "@excalidraw/common";
import { updateFrameMembershipOfSelectedElements, reapplyAutoLayoutToAllFrames } from "@excalidraw/element";
import { InlineIcon } from "../InlineIcon";
import { SpacingHorizontalIcon, SpacingVerticalIcon } from "../icons";
import StatsDragInput from "./DragInput";
import type { DragInputCallbackType } from "./DragInput";

interface SpacingProps {
  elements: readonly ExcalidrawElement[];
  elementsMap: Scene["elementsMap"];
  scene: Scene;
  appState: AppState;
  property: "spacing";
}

// Helper function to trigger auto layout rerendering after spacing changes
// This ensures that child elements within frames are properly repositioned
const triggerAutoLayoutRerendering = (scene: Scene) => {
  // Apply the same layered rerendering pattern used to fix previous auto layout rerender issues
  setTimeout(() => {
    const allElements = scene.getElementsIncludingDeleted();
    const elementsMap = scene.getNonDeletedElementsMap();
    const updatedElements = reapplyAutoLayoutToAllFrames(
      allElements,
      elementsMap,
    );
    if (updatedElements !== allElements) {
      scene.replaceAllElements(updatedElements);

      // Trigger additional auto layout rerendering for nested frames
      setTimeout(() => {
        const currentElements = scene.getElementsIncludingDeleted();
        const currentElementsMap = scene.getNonDeletedElementsMap();
        const rerenderedElements = reapplyAutoLayoutToAllFrames(
          currentElements,
          currentElementsMap,
        );
        if (rerenderedElements !== currentElements) {
          scene.replaceAllElements(rerenderedElements);
        }

        // Final trigger to ensure visual re-rendering
        scene.triggerUpdate();
      }, 0);
    } else {
      // Even if no layout changes, trigger update for visual feedback
      scene.triggerUpdate();
    }
  }, 0);
};

export const Spacing = ({
  elements,
  elementsMap,
  scene,
  appState,
}: SpacingProps) => {
  const targetElements = elements.filter((element) => 
    appState.selectedElementIds[element.id]
  );

  const handleHorizontalSpacingChange: DragInputCallbackType<"x"> = useCallback(
    ({
      accumulatedChange,
      nextValue,
      originalElements,
      originalElementsMap,
      scene,
      app,
    }) => {
      // Filter to get only the originally selected elements
      const originalSelectedElements = originalElements.filter((element) =>
        appState.selectedElementIds[element.id]
      );

      if (originalSelectedElements.length < 2) {
        return;
      }

      let spacingValue: number;

      // Handle direct input (user types value)
      if (nextValue !== undefined) {
        spacingValue = nextValue;
      } else {
        // Handle dragging - get current spacing and add accumulated change
        const currentSpacing = getSpacingBetweenElements(
          originalSelectedElements,
          originalElementsMap,
          "x"
        );
        spacingValue = Math.max(0, currentSpacing + accumulatedChange);
      }

      // Apply spacing to the originally selected elements
      const updatedSelectedElements = applySpacingToElements(
        originalSelectedElements,
        originalElementsMap,
        { axis: "x", value: spacingValue }
      );

      // Get all current elements in scene to preserve unselected ones
      const allCurrentElements = Array.from(scene.getNonDeletedElementsMap().values());
      
      // Create map of updated elements
      const updatedElementsMap = arrayToMap(updatedSelectedElements);
      
      // Merge: use updated elements where available, keep others unchanged
      const finalElements = allCurrentElements.map(
        (element) => updatedElementsMap.get(element.id) || element
      );

      // Update the scene with all elements preserved
      scene.replaceAllElements(finalElements);
      
      // Trigger auto layout rerendering to ensure child elements within frames are repositioned
      triggerAutoLayoutRerendering(scene);
    },
    [appState.selectedElementIds]
  );

  const handleVerticalSpacingChange: DragInputCallbackType<"y"> = useCallback(
    ({
      accumulatedChange,
      nextValue,
      originalElements,
      originalElementsMap,
      scene,
      app,
    }) => {
      // Filter to get only the originally selected elements
      const originalSelectedElements = originalElements.filter((element) =>
        appState.selectedElementIds[element.id]
      );

      if (originalSelectedElements.length < 2) {
        return;
      }

      let spacingValue: number;

      // Handle direct input (user types value)
      if (nextValue !== undefined) {
        spacingValue = nextValue;
      } else {
        // Handle dragging - get current spacing and add accumulated change
        const currentSpacing = getSpacingBetweenElements(
          originalSelectedElements,
          originalElementsMap,
          "y"
        );
        spacingValue = Math.max(0, currentSpacing + accumulatedChange);
      }

      // Apply spacing to the originally selected elements
      const updatedSelectedElements = applySpacingToElements(
        originalSelectedElements,
        originalElementsMap,
        { axis: "y", value: spacingValue }
      );

      // Get all current elements in scene to preserve unselected ones
      const allCurrentElements = Array.from(scene.getNonDeletedElementsMap().values());
      
      // Create map of updated elements
      const updatedElementsMap = arrayToMap(updatedSelectedElements);
      
      // Merge: use updated elements where available, keep others unchanged
      const finalElements = allCurrentElements.map(
        (element) => updatedElementsMap.get(element.id) || element
      );

      // Update the scene with all elements preserved
      scene.replaceAllElements(finalElements);
      
      // Trigger auto layout rerendering to ensure child elements within frames are repositioned
      triggerAutoLayoutRerendering(scene);
    },
    [appState.selectedElementIds]
  );

  // Only show spacing controls when 2 or more elements are selected
  if (targetElements.length < 2) {
    return null;
  }

  const horizontalSpacing = getSpacingBetweenElements(targetElements, elementsMap, "x");
  const verticalSpacing = getSpacingBetweenElements(targetElements, elementsMap, "y");

  return (
    <div className="property-row-dual">
      <div className="property-item">
        <StatsDragInput
          label={<InlineIcon icon={SpacingHorizontalIcon} />}
          elements={targetElements}
          property="x"
          scene={scene}
          appState={appState}
          value={Math.round(horizontalSpacing)}
          dragInputCallback={handleHorizontalSpacingChange}
          dragFinishedCallback={({ app }) => {
            // Capture history after horizontal spacing drag is finished
            app.syncActionResult({
              captureUpdate: CaptureUpdateAction.IMMEDIATELY,
            });
          }}
          tooltip="Horizontal spacing"
          sensitivity={1}
        />
      </div>
      <div className="property-item">
        <StatsDragInput
          label={<InlineIcon icon={SpacingVerticalIcon} />}
          elements={targetElements}
          property="y"
          scene={scene}
          appState={appState}
          value={Math.round(verticalSpacing)}
          dragInputCallback={handleVerticalSpacingChange}
          dragFinishedCallback={({ app }) => {
            // Capture history after vertical spacing drag is finished
            app.syncActionResult({
              captureUpdate: CaptureUpdateAction.IMMEDIATELY,
            });
          }}
          tooltip="Vertical spacing"
          sensitivity={1}
        />
      </div>
    </div>
  );
};

export default Spacing; 